// React and React Router / React y React Router
import { useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Common UI Components / Componentes de UI comunes
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import CartSidebar from './components/cart/CartSidebar';
import ErrorBoundary from './pages/ErrorBoundary';
import MainLayout from './layouts/MainLayout';

// Contexts / Contextos
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './features/auth/context/AuthContext';

// Notifications / Notificaciones
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Optimizaciones de rendimiento
import './styles/catalog-performance.css';

// Standard loaded pages (small or critical) / Páginas cargadas de forma estándar (pequeñas o críticas)
import About from './pages/About';
import AboutTest from './pages/AboutTest';
import Contact from './pages/Contact';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordSetupPage from './pages/PasswordSetupPage';

// Authentication Components / Componentes de autenticación
import ProtectedRoute from './features/auth/components/ProtectedRoute';
import PrivateQuotationRoute from './features/auth/components/PrivateQuotationRoute';
import DashboardLayout from './features/auth/components/DashboardLayout';
import QuotationAuthPage from './features/auth/pages/QuotationAuthPage';
import QuotationListPage from './features/auth/pages/QuotationListPage';
import QuotationDetailPage from './features/auth/pages/QuotationDetailPage';
import QuotationTrackingPage from './features/auth/pages/QuotationTrackingPage';

// Admin Components / Componentes de administración
import AdminLayout from './components/admin/AdminLayout';
import AdminRoute from './components/admin/AdminRoute';
import Categories from './pages/admin/Categories';
import Products from './pages/admin/Products';
import CategoryForm from './pages/admin/CategoryForm';
import ProductForm from './pages/admin/ProductForm';
import Quotations from './pages/admin/Quotations';
import Users from './pages/admin/Users';

// Lazy loaded pages for improved initial performance / Páginas cargadas de forma diferida para mejorar el rendimiento inicial
const Home = lazy(() => import('./pages/Home'));
const Catalog = lazy(() => import('./pages/Catalog'));
const CategoryProducts = lazy(() => import('./pages/CategoryProducts'));
const MedicalProducts = lazy(() => import('./pages/MedicalProducts'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Quote = lazy(() => import('./pages/Quote'));
const QuotationPage = lazy(() => import('./pages/QuotationPage'));
const QuotationConfirmPage = lazy(() => import('./pages/QuotationConfirmPage'));
const QuotationSuccessPage = lazy(() => import('./pages/QuotationSuccessPage'));
// Eliminamos la importación lazy de QuotationTrackPage ya que ahora usamos QuotationTrackingPage

// User Dashboard Pages / Páginas del Dashboard de Usuario
const UserDashboard = lazy(() => import('./pages/user/Dashboard'));
const UserQuotations = lazy(() => import('./pages/user/Quotations'));
const UserQuotationDetail = lazy(() => import('./pages/user/QuotationDetail'));
const UserProfile = lazy(() => import('./pages/user/Profile'));

// Testing Pages / Páginas de prueba
const TestPage = lazy(() => import('./pages/testing/TestPage'));
const QuotationTester = lazy(() => import('./pages/test/QuotationTester'));

function App() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  
  const handleCartOpen = () => {
    setIsCartOpen(true);
  };
  
  const handleCartClose = () => {
    setIsCartOpen(false);
  };
  return (
    <AuthProvider>
      <CartProvider>
        <ToastContainer position="top-right" autoClose={5000} />
        <ScrollToTop />
      <Routes>
        {/* Admin routes - Protected routes / Rutas de administración - Rutas protegidas */}
        <Route path="/admin/*" element={<AdminRoute />}>
          <Route element={<AdminLayout />}>
            <Route index element={
              <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                <div className="container py-4">
                  <h1>Panel de Administración</h1>
                  <p>Bienvenido al panel de administración de VV Health.</p>
                </div>
              </Suspense>
            } />
            <Route path="categories" element={
              <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                <Categories />
              </Suspense>
            } />
            <Route path="categories/new" element={
              <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                <CategoryForm />
              </Suspense>
            } />
            <Route path="categories/edit/:id" element={
              <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                <CategoryForm />
              </Suspense>
            } />
            <Route path="products" element={
              <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                <Products />
              </Suspense>
            } />
            <Route path="products/new" element={
              <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                <ProductForm />
              </Suspense>
            } />
            <Route path="quotations" element={
              <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                <Quotations />
              </Suspense>
            } />
            <Route path="users" element={
              <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                <Users />
              </Suspense>
            } />
          </Route>
        </Route>

        {/* Public routes / Rutas públicas */}
        <Route
          path="/*"
          element={
            <div className="d-flex flex-column min-vh-100">
              <Navbar onCartClick={handleCartOpen} />
              <main className="flex-grow-1">
                <Suspense fallback={<div className="container py-5 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                  <Routes>
                    <Route index element={<Home />} />
                    <Route path="about-test" element={<AboutTest />} />
                    <Route path="about" element={<Navigate to="/about-test" replace />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="catalog" element={<Catalog />} />
                    <Route path="login" element={<LoginPage />} />
                    <Route path="reset-password" element={<ResetPasswordPage />} />
                    <Route path="password-setup" element={<PasswordSetupPage />} />
                    <Route path="category/:categoryId" element={<CategoryProducts />} />
                    <Route path="medical-products" element={<ErrorBoundary><MedicalProducts /></ErrorBoundary>} />
                    <Route path="product/:productId" element={<ProductDetail />} />
                    <Route path="quote" element={<Quote />} />
                    <Route path="quotation" element={<QuotationPage />} />
                    <Route path="quotation/confirm" element={<QuotationConfirmPage />} />
                    <Route path="quotation/success" element={<QuotationSuccessPage />} />
                    {/* Rutas de cotizaciones con magic link - accesibles sin autenticación */}
                    {/* Estas rutas están duplicadas, las eliminamos para evitar conflictos */}
                  </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          }
        />

        {/* Authentication Routes / Rutas de Autenticación */}
        <Route path="quotation/auth/:magicLink" element={<QuotationAuthPage />} />
        
        {/* Magic Link Routes (Public) / Rutas de Magic Link (Públicas) */}
        <Route path="quotation/track/:magicLink" element={
          <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
            <QuotationTrackingPage />
          </Suspense>
        } />
        <Route path="quotation/detail/:magicLink" element={
          <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
            <QuotationDetailPage />
          </Suspense>
        } />
        
        {/* User Dashboard (Protected) / Dashboard de Usuario (Protegido) */}
        <Route path="user">
          <Route 
            index
            element={<Navigate to="/user/dashboard" replace />}
          />
          <Route 
            path="dashboard" 
            element={
              <ProtectedRoute>
                <MainLayout onCartClick={handleCartOpen}>
                  <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                    <UserDashboard />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route 
            path="quotations" 
            element={
              <ProtectedRoute>
                <MainLayout onCartClick={handleCartOpen}>
                  <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                    <UserQuotations />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route 
            path="quotations/:quotationId" 
            element={
              <ProtectedRoute>
                <MainLayout onCartClick={handleCartOpen}>
                  <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                    <UserQuotationDetail />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route 
            path="profile" 
            element={
              <ProtectedRoute>
                <MainLayout onCartClick={handleCartOpen}>
                  <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                    <UserProfile />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
        </Route>
        
        {/* Legacy Quotation Dashboard (Protected) / Dashboard de Cotizaciones Antiguo (Protegido) */}
        <Route 
          path="dashboard" 
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={
            <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
              <QuotationListPage />
            </Suspense>
          } />
          <Route path="quotations" element={
            <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
              <QuotationListPage />
            </Suspense>
          } />
          <Route path="quotation/:magicLink" element={
            <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
              <QuotationDetailPage />
            </Suspense>
          } />
        </Route>
        {/* Testing Routes / Rutas de prueba - Solo disponibles en desarrollo */}
        <Route 
          path="testing" 
          element={
            <MainLayout onCartClick={handleCartOpen}>
              <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                <TestPage />
              </Suspense>
            </MainLayout>
          } 
        />
        
        {/* Quotation Tester - Para probar cotizaciones en tiempo real */}
        <Route 
          path="test/quotations" 
          element={
            <ProtectedRoute>
              <MainLayout onCartClick={handleCartOpen}>
                <Suspense fallback={<div className="container py-4 text-center"><div className="spinner-border text-primary" role="status"></div></div>}>
                  <QuotationTester />
                </Suspense>
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route path="*" element={
          <div className="container py-5 text-center">
            <h1>Página no encontrada</h1>
            <p>La página que estás buscando no existe o ha sido movida.</p>
          </div>
        } />
      </Routes>
      
      {/* Side Cart / Carrito lateral */}
      <CartSidebar isOpen={isCartOpen} onClose={handleCartClose} />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
